import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Stack,
  Container,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  AccountBalance as BankIcon,
  Edit as EditIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getBase64ImageFromUrl = async (imgUrl) => {
  try {
    const response = await fetch(imgUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        resolve(reader.result);
      }, false);
      reader.onerror = () => {
        reject(reader.error);
      };
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    //console.error("Error converting image to base64:", err);
    return null;
  }
};

const formatDateOnly = (dateStr) => {
  if (!dateStr) return 'N/A';
  if (typeof dateStr === 'string' && dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  return dateStr;
};

const ProFormaHistory = () => {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [invoices, setInvoices] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [companyLogoBase64, setCompanyLogoBase64] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  // 3-dot Action Menu State
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeInvoice, setActiveInvoice] = useState(null);

  const handleMenuOpen = (event, inv) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveInvoice(inv);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveInvoice(null);
  };

  // Fetch vendors
  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/contract`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data && response.data.success) {
        setVendors(response.data.data);
      }
    } catch (error) {
      //console.error("Error fetching vendors:", error);
    }
  };

  const [companyDetails, setCompanyDetails] = useState(null);

  // Fetch company details dynamically
  const fetchCompanyDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/api/cmp/current`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data && response.data.success && response.data.data) {
        const company = response.data.data;
        setCompanyDetails(company);
        
        if (company.companyLogo) {
          const logoUrl = company.companyLogo.startsWith('http') ? company.companyLogo : `${API_BASE_URL}/uploads/${company.companyLogo}`;
          const base64 = await getBase64ImageFromUrl(logoUrl);
          if (base64) {
            setCompanyLogoBase64(base64);
          }
        }
      }
    } catch (error) {
      //console.error("Error fetching company details:", error);
    }
  };

  // Fetch logo on mount
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const logoData = await getBase64ImageFromUrl('/logo.png');
        setCompanyLogoBase64(logoData);
      } catch (error) {
        //console.error("Error loading logo for PDF:", error);
      }
    };
    loadLogo();
  }, []);

  // Fetch invoice history
  const fetchInvoiceHistory = async (pageNum = 1) => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/proforma-invoice`, {
        params: {
          page: pageNum,
          limit: limit
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.success) {
        setInvoices(response.data.data);
        setTotalPages(response.data.totalPages || 1);
        setPage(response.data.currentPage || 1);
      }
    } catch (error) {
      //console.error("Error fetching invoice history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch history on mount
  useEffect(() => {
    fetchInvoiceHistory(1);
    fetchVendors();
    fetchCompanyDetails();
  }, []);

  // PDF Generator (Identical layout to ProformaInvoice)
  const generateInvoicePDF = (shouldSave = true, data = null) => {
    if (!data) return null;
    const doc = new jsPDF();
    const currencySym = data.currency === 'INR' ? 'Rs.' : '$';

    // 1. Draw Company Logo
    if (companyLogoBase64) {
      try {
        doc.addImage(companyLogoBase64, 'PNG', 14, 9, 24, 10);
      } catch (err) {
        //console.error("Error drawing logo in PDF:", err);
      }
    }

    // 2. Centered Company Info
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(data.billToCompany || 'PulseHR Solutions Pvt Ltd', 105, 14, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    const addressText = `${data.billToStreet || ''}, ${data.billToCity || ''}`;
    const splitAddress = doc.splitTextToSize(addressText, 110);
    doc.text(splitAddress, 105, 19, { align: 'center' });

    // 3. Top Divider Line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.4);
    doc.line(14, 29, 196, 29);

    // 4. Centered Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(26, 35, 126);
    doc.text('PRO FORMA INVOICE', 105, 35, { align: 'center' });

    // 5. Bottom Divider Line
    doc.line(14, 39, 196, 39);

    // 6. Details Section
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(9);
    
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE DETAILS:', 14, 46);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice No:`, 14, 52);
    doc.setFont('helvetica', 'bold');
    doc.text(data.invoiceNumber, 40, 52);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice Date:`, 14, 57);
    doc.text(data.date, 40, 57);
    
    doc.text(`Due Date:`, 14, 62);
    doc.text(data.dueDate, 40, 62);

    doc.setFont('helvetica', 'bold');
    doc.text('CONTRACT DETAILS:', 110, 46);
    doc.setFont('helvetica', 'normal');
    doc.text(`Consultant:`, 110, 52);
    doc.setFont('helvetica', 'bold');
    doc.text(data.employeeName, 138, 52);
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Designation:`, 110, 57);
    doc.text(data.employeeDesignation, 138, 57);
    
    doc.text(`Period / Mode:`, 110, 62);
    doc.text(`${data.contractPeriod} / Hourly`, 138, 62);

    doc.setDrawColor(220, 220, 220);
    doc.line(14, 68, 196, 68);

    // 7. Billing Parties
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(26, 35, 126);
    doc.text('SERVICE PROVIDER (BILL FROM):', 14, 75);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 50, 50);
    doc.text(data.billFromName, 14, 81);
    doc.text(data.billFromStreet, 14, 86);
    doc.text(`Phone: ${data.billFromPhone}`, 14, 91);
    doc.text(`Email: ${data.billFromEmail}`, 14, 96);
    if (data.billFromGst) doc.text(`PAN/GST: ${data.billFromGst}`, 14, 101);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(26, 35, 126);
    doc.text('CLIENT / BILL TO:', 110, 75);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 50, 50);
    doc.text(data.billToCompany, 110, 81);
    
    const clientAddress = `${data.billToStreet}, ${data.billToCity}`;
    const splitClientAddress = doc.splitTextToSize(clientAddress, 86);
    doc.text(splitClientAddress, 110, 86);
    
    const clientAddressHeight = splitClientAddress.length * 4.5;
    
    doc.text(`Phone: ${data.billToPhone}`, 110, 86 + clientAddressHeight);
    doc.text(`Email: ${data.billToEmail}`, 110, 91 + clientAddressHeight);
    if (data.billToGst) doc.text(`GSTIN: ${data.billToGst}`, 110, 96 + clientAddressHeight);

    const billingSectionEndY = Math.max(
      data.billFromGst ? 101 : 96,
      96 + clientAddressHeight + (data.billToGst ? 5 : 0)
    );
    
    const divider1Y = billingSectionEndY + 5;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.4);
    doc.line(14, divider1Y, 196, divider1Y);

    // 8. Bank Details
    const bankDetailsStartY = divider1Y + 7;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(26, 35, 126);
    doc.text('BANK DETAILS (FOR REMITTANCE):', 14, bankDetailsStartY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 50, 50);
    doc.text(`Bank Name: ${data.bankName}`, 14, bankDetailsStartY + 6);
    doc.text(`Account Name: ${data.accountName}`, 14, bankDetailsStartY + 11);
    doc.text(`Account No: ${data.accountNumber}`, 14, bankDetailsStartY + 16);
    doc.text(`IFSC Code: ${data.ifscCode}`, 14, bankDetailsStartY + 21);

    const divider2Y = bankDetailsStartY + 27;
    doc.line(14, divider2Y, 196, divider2Y);

    // 9. Table
    const tableHeaders = [['Description', 'Billing Rate', 'Quantity/Hours', 'Subtotal']];
    const tableRows = [
      [
        `Professional Services & Deliverables rendered by ${data.employeeName} (${data.employeeDesignation})`,
        `${currencySym} ${parseFloat(data.hourlyRate).toFixed(2)} / hr`,
        `${data.totalHours} hrs`,
        `${currencySym} ${parseFloat(data.contractTotal).toFixed(2)}`
      ]
    ];

    doc.autoTable({
      startY: divider2Y + 6,
      head: tableHeaders,
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [26, 35, 126], textColor: [255, 255, 255] },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 90 },
        1: { cellWidth: 30, halign: 'right' },
        2: { cellWidth: 35, halign: 'center' },
        3: { cellWidth: 27, halign: 'right' },
      }
    });

    let currentY = doc.lastAutoTable.finalY + 10;
    const summaryXLabel = 120;
    const summaryXVal = 196;

    const printRow = (label, val, y, isBold = false) => {
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.setFontSize(isBold ? 9.5 : 8.5);
      doc.setTextColor(isBold ? 0 : 80, isBold ? 0 : 80, isBold ? 0 : 80);
      doc.text(label, summaryXLabel, y);
      doc.text(val, summaryXVal, y, { align: 'right' });
    };

    const sub = parseFloat(data.contractTotal) || 0;
    const gst = sub * ((parseFloat(data.gstRate) || 0) / 100);
    const tds = sub * ((parseFloat(data.tdsRate) || 0) / 100);
    const other = parseFloat(data.otherChargesValue) || 0;
    const tot = sub + gst - tds + other;

    printRow('Service Subtotal:', `${currencySym} ${sub.toFixed(2)}`, currentY);
    printRow(`GST (${data.gstRate}%):`, `(+) ${currencySym} ${gst.toFixed(2)}`, currentY + 5);
    printRow(`TDS Deductions (${data.tdsRate}%):`, `(-) ${currencySym} ${tds.toFixed(2)}`, currentY + 10);
    if (other > 0) {
      printRow(`${data.otherChargesLabel || 'Other'}:`, `(+) ${currencySym} ${other.toFixed(2)}`, currentY + 15);
    }
    
    doc.setFillColor(245, 245, 245);
    doc.rect(118, currentY + 19, 80, 8, 'F');
    printRow('Net Payable Amount:', `${currencySym} ${tot.toFixed(2)}`, currentY + 24, true);

    if (shouldSave) {
      doc.save(`proforma_${data.invoiceNumber}.pdf`);
    }
    return doc;
  };

  const mapInvoiceToPDFData = (inv) => {
    const isVendor = inv.invoiceNumber?.startsWith('VEND-');

    if (isVendor) {
      const vendor = vendors.find(v => v.id === inv.contractEmployee?.companyId);
      return {
        invoiceNumber: inv.invoiceNumber,
        date: formatDateOnly(inv.invoiceDate),
        dueDate: formatDateOnly(inv.dueDate),
        currency: 'INR',
        
        // Dynamic company details from database
        billToCompany: companyDetails?.companyname || 'Company Name',
        billToStreet: companyDetails?.address || 'Street Address',
        billToCity: companyDetails?.hq || 'City, State',
        billToPhone: companyDetails?.contactNumber || 'Phone Number',
        billToEmail: companyDetails?.contactemail || 'Email Address',
        billToGst: companyDetails?.GST || 'GSTIN',
        
        // Bill From (Agency Corporate Details)
        billFromName: vendor ? vendor.companyName : (inv.contractEmployee?.fullName || 'Vendor Agency'),
        billFromStreet: vendor ? vendor.companyAddress : 'N/A',
        billFromCity: 'Corporate Headquarters',
        billFromPhone: 'N/A',
        billFromEmail: 'N/A',
        billFromGst: vendor ? vendor.companyPAN : 'N/A',
        
        // Bank Details (Agency Corporate Bank Details)
        bankName: vendor ? vendor.bankName : 'N/A',
        accountName: vendor ? vendor.companyName : 'N/A',
        accountNumber: vendor ? vendor.accountNumber : 'N/A',
        ifscCode: vendor ? vendor.ifscCode : 'N/A',
        
        // Contractor info
        employeeName: vendor ? vendor.companyName : 'Vendor Agency',
        employeeDesignation: 'Consolidated Agency Payout',
        contractPeriod: inv.contractEmployee?.contractPeriod || 'N/A',
        hourlyRate: inv.hourlyRate,
        totalHours: inv.totalHours,
        contractTotal: inv.subtotal,
        
        // Financials & Taxes (2% TDS under Section 194C)
        gstRate: inv.subtotal > 0 ? ((inv.gstAmount / inv.subtotal) * 100).toFixed(0) : '18',
        tdsRate: inv.subtotal > 0 ? ((inv.tdsAmount / inv.subtotal) * 100).toFixed(0) : '2',
        otherChargesLabel: 'Technology Fee',
        otherChargesValue: '0',
      };
    }

    const addressVal = inv.contractEmployee?.address || '';
    let street = addressVal;
    let cityStatePin = '';
    
    if (addressVal) {
      const parts = addressVal.split(',');
      if (parts.length >= 3) {
        cityStatePin = parts.slice(-3).join(',').trim();
        street = parts.slice(0, -3).join(',').trim();
      } else if (parts.length === 2) {
        cityStatePin = parts[1].trim();
        street = parts[0].trim();
      }
    } else {
      street = inv.contractEmployee?.department || 'N/A';
    }

    return {
      invoiceNumber: inv.invoiceNumber,
      date: formatDateOnly(inv.invoiceDate),
      dueDate: formatDateOnly(inv.dueDate),
      currency: 'INR',
      
      // Dynamic company details from database
      billToCompany: companyDetails?.companyname || 'Company Name',
      billToStreet: companyDetails?.address || 'Street Address',
      billToCity: companyDetails?.hq || 'City, State',
      billToPhone: companyDetails?.contactNumber || 'Phone Number',
      billToEmail: companyDetails?.contactemail || 'Email Address',
      billToGst: companyDetails?.GST || 'GSTIN',

      // Bill From (Contractor Profile)
      billFromName: inv.contractEmployee?.fullName || 'N/A',
      billFromStreet: street,
      billFromCity: cityStatePin,
      billFromPhone: inv.contractEmployee?.phoneNumber || '',
      billFromEmail: inv.contractEmployee?.email || '',
      billFromGst: inv.contractEmployee?.panNumber || '',

      // Bank Details
      bankName: inv.contractEmployee?.bankName || '',
      accountName: inv.contractEmployee?.accountName || '',
      accountNumber: inv.contractEmployee?.accountNumber || '',
      ifscCode: inv.contractEmployee?.ifscCode || '',

      // Contractor info
      employeeName: inv.contractEmployee?.fullName || 'N/A',
      employeeDesignation: inv.contractEmployee?.designation || 'Consultant',
      contractPeriod: inv.contractEmployee?.contractPeriod || 'N/A',
      hourlyRate: inv.hourlyRate,
      totalHours: inv.totalHours,
      contractTotal: inv.subtotal,

      // Financials & Taxes
      gstRate: inv.subtotal > 0 ? ((inv.gstAmount / inv.subtotal) * 100).toFixed(0) : '18',
      tdsRate: inv.subtotal > 0 ? ((inv.tdsAmount / inv.subtotal) * 100).toFixed(0) : '10',
      otherChargesLabel: 'Technology Fee',
      otherChargesValue: '0',
    };
  };

  const handleDownloadHistory = (inv) => {
    const pdfData = mapInvoiceToPDFData(inv);
    generateInvoicePDF(true, pdfData);
  };

  const handleEditInvoice = (inv) => {
    navigate('/proforma-invoice', { state: { invoiceToEdit: inv } });
  };

  const handleSendInvoice = async (inv) => {
    if (!window.confirm(`Are you sure you want to send Pro Forma Invoice ${inv.invoiceNumber} to the employee?`)) {
      return;
    }
    
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setSnackbar({
          open: true,
          message: 'No authentication token found. Please log in.',
          severity: 'error'
        });
        setHistoryLoading(false);
        return;
      }

      // Generate PDF
      const pdfData = mapInvoiceToPDFData(inv);
      const pdfDoc = generateInvoicePDF(false, pdfData);
      const pdfBase64 = pdfDoc.output('datauristring');

      const response = await axios.post(`${API_BASE_URL}/api/proforma-invoice/${inv.invoice_id}/send`, {
        pdfFile: pdfBase64
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data && response.data.success) {
        setSnackbar({
          open: true,
          message: 'Pro Forma Invoice sent to employee successfully!',
          severity: 'success'
        });
        // Refresh the invoices list
        fetchInvoiceHistory(page);
      } else {
        throw new Error(response.data.message || 'Failed to send invoice email.');
      }
    } catch (error) {
      //console.error("Error sending invoice email:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to send invoice email.',
        severity: 'error'
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm("Are you sure you want to delete this invoice?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/proforma-invoice/${invoiceId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSnackbar({
        open: true,
        message: 'Invoice deleted successfully.',
        severity: 'success'
      });
      fetchInvoiceHistory(page);
    } catch (error) {
      //console.error("Error deleting invoice:", error);
      setSnackbar({
        open: true,
        message: 'Failed to delete invoice.',
        severity: 'error'
      });
    }
  };

  const handleUpdateStatus = async (invoiceId, currentStatus) => {
    const nextStatus = currentStatus === 'draft' ? 'paid' : 'draft';
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_BASE_URL}/api/proforma-invoice/${invoiceId}/status`, {
        status: nextStatus
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSnackbar({
        open: true,
        message: `Invoice status marked as ${nextStatus.toUpperCase()}.`,
        severity: 'success'
      });
      fetchInvoiceHistory(page);
    } catch (error) {
      //console.error("Error updating invoice status:", error);
      setSnackbar({
        open: true,
        message: 'Failed to update invoice status.',
        severity: 'error'
      });
    }
  };

  return (
   <Container maxWidth={false} sx={{ mt: 3, width: '100%' }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Title & Header Section */}
      {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a237e' }}>
            Pro Forma Invoice Panel
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            View, download, and manage previously generated contractor invoices and compliance records
          </Typography>
        </Box>
      </Box> */}

      {/* Invoice History Section */}
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3, borderTop: '6px solid #1a237e' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ color: '#1a237e' }}>
              Generated Invoice History
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Review and manage historical payouts sent to direct contractors
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {historyLoading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={40} sx={{ color: '#1a237e' }} />
          </Box>
        ) : invoices.length === 0 ? (
          <Box py={5} textAlign="center">
            <Typography variant="body1" color="text.secondary">
              No invoices generated yet. Generate your first invoice using the sidebar link!
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Box} sx={{ overflow: 'visible' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9' }}>Invoice No.</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9' }}>Payout Type</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9' }}>Contractor/Agency Name</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9' }}>Date</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9' }}>Due Date</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9' }}>Hours</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', textAlign: 'right' }}>Net Payable</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', textAlign: 'center' }}>Status</TableCell>
                  <TableCell style={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.invoice_id} hover>
                    <TableCell style={{ fontWeight: 'bold' }}>{inv.invoiceNumber}</TableCell>
                    <TableCell>
                      {inv.invoiceNumber?.startsWith('VEND-') ? (
                        <Chip
                          size="small"
                          label="Vendor Agency"
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: '#eff6ff',
                            color: '#1e40af',
                            border: '1px solid #bfdbfe'
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Direct Contractor"
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: '#f5f3ff',
                            color: '#5b21b6',
                            border: '1px solid #ddd6fe'
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {inv.invoiceNumber?.startsWith('VEND-')
                        ? (vendors.find(v => v.id === inv.contractEmployee?.companyId)?.companyName || 'Vendor Agency')
                        : (inv.contractEmployee?.fullName || 'Unknown Contractor')}
                    </TableCell>
                    <TableCell>{formatDateOnly(inv.invoiceDate)}</TableCell>
                    <TableCell>{formatDateOnly(inv.dueDate)}</TableCell>
                    <TableCell>{inv.totalHours} hrs</TableCell>
                    <TableCell align="right" style={{ fontWeight: 'bold' }}>
                      ₹{inv.netPayable?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={inv.status?.toUpperCase()}
                        color={inv.status === 'paid' ? 'success' : 'warning'}
                        variant="outlined"
                        onClick={() => handleUpdateStatus(inv.invoice_id, inv.status)}
                        sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleMenuOpen(e, inv)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => fetchInvoiceHistory(value)}
              color="primary"
              shape="rounded"
              size="medium"
            />
          </Box>
        )}
      </Paper>

      {/* 3-dot Action Dropdown Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180,
            borderRadius: 2,
            mt: 0.5,
            '& .MuiMenuItem-root': {
              fontSize: '0.85rem',
              py: 1,
            }
          }
        }}
      >
        {activeInvoice && (
          <Box>
            {/* Send to Contractor / Vendor */}
            <MenuItem 
              onClick={() => {
                handleSendInvoice(activeInvoice);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <SendIcon fontSize="small" color="info" />
              </ListItemIcon>
              <ListItemText 
                primary={
                  activeInvoice.invoiceNumber?.startsWith('VEND-') 
                    ? "Send to Vendor" 
                    : "Send to Contractor"
                } 
              />
            </MenuItem>
            
            {/* Edit Invoice */}
            {!activeInvoice.isSent && activeInvoice.status !== 'paid' && (
              <MenuItem 
                onClick={() => {
                  handleEditInvoice(activeInvoice);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" color="secondary" />
                </ListItemIcon>
                <ListItemText primary="Edit Invoice" />
              </MenuItem>
            )}
            
            {/* Download PDF */}
            <MenuItem 
              onClick={() => {
                handleDownloadHistory(activeInvoice);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <DownloadIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="Download PDF" />
            </MenuItem>
            
            {/* Toggle Status (Paid/Draft) */}
            {activeInvoice.status !== 'paid' && (
              <MenuItem 
                onClick={() => {
                  handleUpdateStatus(activeInvoice.invoice_id, activeInvoice.status);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <CheckIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primary="Mark as Paid" />
              </MenuItem>
            )}
            
      
            
           
          </Box>
        )}
      </Menu>
    </Container>
  );
};

export default ProFormaHistory;
